import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import {
  type ImportKind,
  type ExportJob,
  type ImportJob,
} from '@nucleus-commerce/api-client/admin';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { useStore } from '@/shared/store/store-context';

import { useExportColumns } from '../components/export-columns';
import { ExportFormCard } from '../components/export-form-card';
import { useImportColumns } from '../components/import-columns';
import { ImportErrorsSheet } from '../components/import-errors-sheet';
import { ImportFormCard } from '../components/import-form-card';
import { jobFilters } from '../components/job-filters';
import { useDownloadExport } from '../hooks/use-download-export';
import { useExportJobs } from '../hooks/use-export-jobs';
import { useImportJobs } from '../hooks/use-import-jobs';
import {
  CLEAR_JOB_LIST,
  isImportKind,
  type ImportsExportsSearch,
  type JobTab,
} from '../schemas/imports-exports-search';

const ROUTE_ID = '/_authed/_active/s/$storeId/settings/imports-exports' as const;
const ROUTE_PATH = '/s/$storeId/settings/imports-exports' as const;

/** Whether filters or paging narrow the list, so an empty page is not an empty history. */
const narrowed = (list: SearchListState) =>
  Object.keys(list.filters).length > 0 || Boolean(list.cursor);

interface JobsTableProps {
  storeId: string;
  search: ImportsExportsSearch;
  onSearchChange: (patch: SearchPatch) => void;
}

function ExportJobsTable({ storeId, search, onSearchChange }: JobsTableProps) {
  const { t } = useTranslation('importsExports');
  const filters = useMemo(() => jobFilters<ExportJob>({ t, tab: 'exports' }), [t]);
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useExportJobs(storeId, list.params);
  const download = useDownloadExport(storeId);
  const { mutate: startDownload } = download;
  const onDownload = useCallback((job: ExportJob) => startDownload(job), [startDownload]);
  const columns = useExportColumns(
    onDownload,
    download.isPending ? download.variables.id : undefined,
  );
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'export-jobs',
  });

  return (
    <DataTable
      table={table}
      search={false}
      loading={query.isPending}
      fetching={query.isFetching}
      error={query.error}
      onRetry={() => void query.refetch()}
      getRowLabel={(row) => t(`kinds.${row.kind}`)}
      resourceName={{ singular: t('resource.export.one'), plural: t('resource.export.other') }}
      emptyState={{ title: narrowed(list) ? t('noMatches') : t('export.empty') }}
      tour={{ table: 'settings.imports-exports.export-jobs' }}
    />
  );
}

function ImportJobsTable({
  storeId,
  search,
  onSearchChange,
  onOpen,
}: JobsTableProps & { onOpen: (job: ImportJob) => void }) {
  const { t } = useTranslation('importsExports');
  const filters = useMemo(() => jobFilters<ImportJob>({ t, tab: 'imports' }), [t]);
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useImportJobs(storeId, list.params);
  const columns = useImportColumns(onOpen);
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'import-jobs',
  });

  return (
    <DataTable
      table={table}
      search={false}
      loading={query.isPending}
      fetching={query.isFetching}
      error={query.error}
      onRetry={() => void query.refetch()}
      onRowClick={onOpen}
      getRowLabel={(row) => t(`kinds.${row.kind}`)}
      resourceName={{ singular: t('resource.import.one'), plural: t('resource.import.other') }}
      emptyState={{ title: narrowed(list) ? t('noMatches') : t('import.empty') }}
    />
  );
}

function ImportsExports() {
  const { t } = useTranslation('importsExports');
  const store = useStore();
  const { can } = usePermissions();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const [openImportId, setOpenImportId] = useState<string | null>(null);

  const canExport = can('exports.run');
  const canImport = can('imports.run');
  const tab: JobTab = search.tab ?? (canExport ? 'exports' : 'imports');

  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const openImport = useCallback((job: ImportJob) => setOpenImportId(job.id), []);

  const importKind: ImportKind | undefined =
    search.new && isImportKind(search.new) ? search.new : undefined;

  return (
    <PageContent>
      <PageHeader title={t('title')} description={t('description', { store: store.name })} />
      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              ...CLEAR_JOB_LIST,
              tab: value,
              new: undefined,
            }),
          })
        }
      >
        <TabsList aria-label={t('tabs.label')} data-tour="settings.imports-exports.tabs">
          {canExport ? <TabsTrigger value="exports">{t('tabs.exports')}</TabsTrigger> : null}
          {canImport ? (
            <TabsTrigger value="imports" data-tour="settings.imports-exports.imports-tab">
              {t('tabs.imports')}
            </TabsTrigger>
          ) : null}
        </TabsList>
        {canExport ? (
          <TabsContent value="exports" className="grid gap-4 pt-2 lg:grid-cols-5">
            <div className="lg:col-span-2" data-tour="settings.imports-exports.export-form">
              <ExportFormCard
                key={search.new ?? 'none'}
                storeId={store.id}
                defaultKind={search.new}
              />
            </div>
            <div className="lg:col-span-3">
              <ExportJobsTable storeId={store.id} search={search} onSearchChange={onSearchChange} />
            </div>
          </TabsContent>
        ) : null}
        {canImport ? (
          <TabsContent value="imports" className="grid gap-4 pt-2 lg:grid-cols-5">
            <div className="lg:col-span-2" data-tour="settings.imports-exports.import-form">
              <ImportFormCard
                key={importKind ?? 'none'}
                storeId={store.id}
                defaultKind={importKind}
                onOpenJob={openImport}
              />
            </div>
            <div className="lg:col-span-3">
              <ImportJobsTable
                storeId={store.id}
                search={search}
                onSearchChange={onSearchChange}
                onOpen={openImport}
              />
            </div>
          </TabsContent>
        ) : null}
      </Tabs>
      <ImportErrorsSheet
        storeId={store.id}
        jobId={openImportId}
        onClose={() => setOpenImportId(null)}
      />
    </PageContent>
  );
}

export function ImportsExportsPage() {
  return (
    <FeatureErrorBoundary name="imports-exports">
      <ImportsExports />
    </FeatureErrorBoundary>
  );
}
